import React, { useState, useEffect, useCallback, useMemo } from 'react';
import InvoiceGrid from './InvoiceGrid';
import InvoiceModal from './InvoiceModal';
import InvoicesPreview from './InvoicesPreview';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import ViewToolbar from '../../common/ViewToolbar';
import CustomerModal from '../customers/CustomerModal';
import { useModal } from '../../../hooks/useModal';
import { usePeriodFilter } from '../../../hooks/usePeriodFilter';
import { useDebounce } from '../../../hooks/useDebounce';
import * as invoiceService from '../../../services/invoiceService';
import { fetchPaymentMethods, recordPayment, deletePayment } from '../../../services/paymentService';
import * as customerService from '../../../services/customerService';

/**
 * Self-contained Invoices feature view.
 *
 * The most complex view because it includes:
 *  - The invoice grid listing
 *  - The invoice modal (create/edit)
 *  - The invoice preview sub-view (PDF viewer)
 *  - Draft-restore logic (when returning from preview back to the modal)
 *
 * Before: all of this was interleaved in Dashboard.js alongside 5 other entities.
 * After:  a single cohesive component that owns everything invoice-related.
 */
export default function InvoicesView({ importFromQuote, onClearImport }) {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [setup, setSetup] = useState([]);
  const [setup2, setSetup2] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [gridApi, setGridApi] = useState(null);

  // Sub-view state: 'grid' (default) or 'preview'
  const [subView, setSubView] = useState('grid');
  const [draftInvoice, setDraftInvoice] = useState(null);
  const [draftToRestore, setDraftToRestore] = useState(null);
  const [createdCustomerId, setCreatedCustomerId] = useState(null);

  const editModal = useModal();
  const deleteModal = useModal();
  const createCustomerModal = useModal();
  const periodFilter = usePeriodFilter();

  const filteredInvoices = useMemo(
    () => periodFilter.filterByPeriod(invoices, 'invoice_date'),
    [invoices, periodFilter],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await invoiceService.fetchInvoices(debouncedSearchTerm);
      setInvoices(result.invoices);
      setCustomers(result.customers);
      setItems(result.items);
      setTaxes(result.taxes);
      setSetup(result.setup);
      setSetup2(result.setup2);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }

    // Fetch payment methods separately so a failure here
    // doesn't block the invoice grid from loading
    try {
      const methods = await fetchPaymentMethods();
      setPaymentMethods(methods);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!loading && importFromQuote) {
      setDraftToRestore(importFromQuote);
      editModal.open(null);
      onClearImport?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, importFromQuote]);

  const handleSave = async (formData) => {
    try {
      await invoiceService.saveInvoice(formData, editModal.record?.invoice_id || null);
      editModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await invoiceService.deleteInvoice(deleteModal.record.invoice_id);
      deleteModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleAddPayment = async (paymentData, invoice) => {
    await recordPayment(paymentData, invoice);
    fetchData(); // refresh grid to reflect the new payment
  };

  const handleDeletePayment = async (paymentId, invoiceId) => {
    await deletePayment(paymentId, invoiceId);
    fetchData(); // refresh grid to reflect the removed payment
  };

  const handleCreateCustomer = async (formData) => {
    try {
      const created = await customerService.createCustomer(formData);
      setCustomers((prev) => {
        const next = [...(prev || []), created];
        next.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
        return next;
      });
      setCreatedCustomerId(created?.id ?? null);
      createCustomerModal.close();
    } catch (err) {
      alert('Failed to save: ' + (err.message || String(err)));
    }
  };

  // Preview flow: modal -> preview
  const handlePreview = (draft) => {
    setDraftInvoice(draft);
    setSubView('preview');
    editModal.close();
  };

  // Back from preview -> reopen modal with draft data
  const handleBackFromPreview = (draft) => {
    setSubView('grid');
    setDraftToRestore(draft);
    // Reopen the modal with the original record (if editing)
    const record = draft?.formData?.invoice_id != null
      ? invoices.find((inv) => inv.invoice_id === draft.formData.invoice_id) ?? null
      : null;
    editModal.open(record);
  };

  // Preview sub-view
  if (subView === 'preview') {
    return (
      <InvoicesPreview
        draftInvoice={draftInvoice}
        onBack={() => handleBackFromPreview(draftInvoice)}
      />
    );
  }

  // Grid view (default)
  return (
    <>
      <ViewToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => editModal.open(null)}
        onExportCsv={() => gridApi?.exportToCsv()}
        placeholder="Search invoices..."
        addLabel="Create Invoice"
        addColor="bg-emerald-500 hover:bg-emerald-600"
        period={periodFilter.period}
        onPeriodChange={periodFilter.onPeriodChange}
        dateFrom={periodFilter.dateFrom}
        onDateFromChange={periodFilter.onDateFromChange}
        dateTo={periodFilter.dateTo}
        onDateToChange={periodFilter.onDateToChange}
      />

      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="flex justify-center mt-10">Loading...</div>
        ) : (
          <InvoiceGrid
            rowData={filteredInvoices}
            onEdit={(r) => editModal.open(r)}
            onDelete={(r) => deleteModal.open(r)}
            onGridReady={setGridApi}
          />
        )}
      </div>

      <InvoiceModal
        isOpen={editModal.isOpen}
        onClose={() => { editModal.close(); setDraftToRestore(null); }}
        onSubmit={handleSave}
        initialData={editModal.record}
        draftToRestore={draftToRestore}
        onClearDraftRestore={() => setDraftToRestore(null)}
        customers={customers}
        items={items}
        taxes={taxes}
        setup={setup}
        setup2={setup2}
        onPreview={handlePreview}
        paymentMethods={paymentMethods}
        onAddPayment={handleAddPayment}
        onDeletePayment={handleDeletePayment}
        payments={invoices.flatMap((inv) => inv.payments || [])}
        onCreateCustomer={() => createCustomerModal.open(null)}
        createdCustomerId={createdCustomerId}
        onQuickBooksSynced={(patch) => {
          fetchData();
          if (
            patch?.invoice_id != null &&
            editModal.isOpen &&
            editModal.record?.invoice_id === patch.invoice_id
          ) {
            editModal.open({
              ...editModal.record,
              quickbooks_invoice_id: patch.quickbooks_invoice_id ?? editModal.record.quickbooks_invoice_id,
              quickbooks_synced_at: patch.quickbooks_synced_at ?? editModal.record.quickbooks_synced_at,
            });
          }
        }}
        onXeroSynced={(patch) => {
          fetchData();
          if (
            patch?.invoice_id != null &&
            editModal.isOpen &&
            editModal.record?.invoice_id === patch.invoice_id
          ) {
            editModal.open({
              ...editModal.record,
              xero_invoice_id: patch.xero_invoice_id ?? editModal.record.xero_invoice_id,
              xero_synced_at: patch.xero_synced_at ?? editModal.record.xero_synced_at,
            });
          }
        }}
      />

      <CustomerModal
        isOpen={createCustomerModal.isOpen}
        onClose={createCustomerModal.close}
        onSubmit={handleCreateCustomer}
        initialData={createCustomerModal.record}
        title="Add New Customer"
        existingCustomers={customers}
      />

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        recordName={
          deleteModal.record?.invoice_number
            ? `Invoice #${deleteModal.record.invoice_number}`
            : 'this invoice'
        }
      />
    </>
  );
}
